import { useVideos } from '@features/dashboard/widgets/videos/hooks/useVideos';
import { useApi } from '@hooks/useApi';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useState, useEffect, useMemo, useTransition } from 'react';
import { useFreemiumVideos } from './useFreemiumVideos';
import { MediaContent, Show, MediaGenre, NavigationLevel } from '../types/types';
import { toastifyService } from '@services/ToastifyService';
import { checkIsFreemiumUser } from '@utils/permissions';

export const useFreemiumNews = () => {
  const { apiClient, loading: isApiLoading, error: apiError } = useApi();
  const [latestVids, setLatestVids] = useState<MediaContent[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [mute, setMute] = useState(true);
  const [isLive, setIsLive] = useState(true);
  const [selectedProgramme, setSelectedProgramme] = useState<Show | null>(null);
  const [showVideos, setShowVideos] = useState<MediaContent[]>([]);
  const [navigationLevel, setNavigationLevel] = useState<NavigationLevel>(NavigationLevel.CATEGORIES);
  const [mediaGenres, setMediaGenres] = useState<MediaGenre[]>([]);
  const [source, setSource] = useState<string>('youtube');
  const [fromProgrammesGrid, setFromProgrammesGrid] = useState(false);
  const [hasShownError, setHasShownError] = useState(false);
  const [selectedVideoTitle, setSelectedVideoTitle] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const mediaContentLoader = useMemo(() => apiClient?.mediaContentLoader(), [apiClient]);
  const user = useUserContext();
  const isFreemiumUser = checkIsFreemiumUser(user);
  const [ignored, startTransition] = useTransition();

  const handleError = (error: any) => {
    if (hasShownError) return;
    setHasShownError(true);
    toastifyService.showError('An error has occured while loading the news page, please try again later.');
    console.error(error);
  };

  const {
    videos: mediaContent,
    hasMore: hasMoreMediaContent,
    loadMoreVideos: loadMoreMediaContent,
    loadVideosFromQuery,
    loadInitialVideos,
    searchVideos,
  } = useFreemiumVideos(mediaContentLoader!);

  const videosLoader = apiClient?.videosLoader();
  const { selectedVideoId, handleVideoClick, newVideoIds, isLoading } = useVideos(videosLoader);
  const isApiAvailable = apiClient !== undefined && !apiError;

  useEffect(() => {
    const fetchCategories = async () => {
      const categories = await apiClient?.getMediaContentCategories();
      setMediaGenres(categories || ([] as MediaGenre[]));
    };

    try {
      fetchCategories();
    } catch (error) {
      handleError(error);
    }
  }, [apiClient]);

  useEffect(() => {
    if (!apiClient) return;
    loadInitialVideos();
  }, [apiClient]);

  useEffect(() => {
    if (latestVids.length === 0 && mediaContent.length >= 10) {
      setLatestVids(mediaContent.slice(0, 10));
    }
  }, [mediaContent.length]);

  useEffect(() => {
    if (navigationLevel === NavigationLevel.VIDEOS && selectedProgramme) {
      const videosGrid = document.querySelector('[data-videos-grid]');
      if (videosGrid) {
        videosGrid.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [navigationLevel, selectedProgramme]);

  useEffect(() => {
    if (navigationLevel !== NavigationLevel.SEARCH) {
      setSearchQuery('');
    }
  }, [navigationLevel]);

  const onVideoClick = (videoId: string, source: string) => {
    if (mute) setMute(false);
    setIsLive(false);
    handleVideoClick(videoId);
    setSource(source);
    const video = mediaContent.find(v => v.id === videoId) || latestVids.find(v => v.id === videoId);
    if (video) {
      setSelectedVideoTitle(video.title);
    }
    const iframe = document.querySelector(`iframe`);
    if (iframe) {
      iframe.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const onProgrammeClick = async (programme: Show, fromShowsGrid: boolean = false) => {
    setSelectedProgramme(programme);
    setNavigationLevel(NavigationLevel.VIDEOS);
    setFromProgrammesGrid(fromShowsGrid);
    const currentGenre = mediaGenres.find(genre => genre.shows.some(s => s.name === programme.name));
    await loadVideosFromQuery({ genre: currentGenre?.name || '', show: programme.name });
  };

  const handleBackClick = () => {
    const resetCommonState = () => {
      setSelectedProgramme(null);
      setShowVideos([]);
    };

    switch (navigationLevel) {
      case NavigationLevel.VIDEOS:
        resetCommonState();
        setNavigationLevel(fromProgrammesGrid ? NavigationLevel.SHOWS : NavigationLevel.CATEGORIES);
        break;

      case NavigationLevel.SHOWS:
        resetCommonState();
        setNavigationLevel(NavigationLevel.CATEGORIES);
        break;

      case NavigationLevel.SEARCH:
        resetCommonState();
        setNavigationLevel(NavigationLevel.CATEGORIES);
        setSearchQuery('');
        break;

      case NavigationLevel.CATEGORIES:
        break;
    }
  };

  const onLiveClick = () => {
    setNavigationLevel(NavigationLevel.CATEGORIES);
    setSelectedProgramme(null);
    setShowVideos([]);
    setIsLive(true);
    handleVideoClick('');
    const iframe = document.querySelector(`iframe`);
    if (iframe) {
      iframe.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const onCategoryClick = (category: string) => {
    setSelectedCategory(category);
    const genreData = mediaGenres.find(genre => genre.name === category);
    if (genreData && genreData.shows.length > 0) {
      setNavigationLevel(NavigationLevel.SHOWS);
      const showsGrid = document.querySelector('[data-shows-grid]');
      if (showsGrid) {
        showsGrid.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    } else {
      setNavigationLevel(NavigationLevel.CATEGORIES);
    }
    setSelectedProgramme(null);
    setShowVideos([]);
  };

  const onSearch = (query: string) => {
    setSearchQuery(query);

    if (query.length > 0) {
      startTransition(() => {
        if (navigationLevel !== NavigationLevel.SEARCH) {
          setNavigationLevel(NavigationLevel.SEARCH);
        }
        if (selectedProgramme) setSelectedProgramme(null);
        if (showVideos.length > 0) setShowVideos([]);
      });
      searchVideos(query);
    } else {
      startTransition(() => {
        setNavigationLevel(NavigationLevel.CATEGORIES);
        setSelectedProgramme(null);
        setShowVideos([]);
      });
    }
  };

  return {
    latestVids,
    selectedCategory,
    mute,
    isLive,
    selectedProgramme,
    showVideos,
    navigationLevel,
    mediaGenres,
    source,
    fromProgrammesGrid,
    mediaContent,
    hasMoreMediaContent,
    selectedVideoId,
    newVideoIds,
    isLoading,
    loadMoreMediaContent,
    loadVideosFromQuery,
    loadInitialVideos,
    isFreemiumUser,
    onVideoClick,
    onProgrammeClick,
    handleBackClick,
    onLiveClick,
    onCategoryClick,
    isApiAvailable,
    isApiLoading,
    apiError,
    selectedVideoTitle,
    onSearch,
    searchQuery,
  };
};
